// Het bonnetje onder een chatantwoord.
//
// "Toon het bonnetje, niet de gedachten" (diepteanalyse 2026-08-25, §5). De
// redeneertekst van het model tonen voegt niets toe: het is geen bewijs, en de
// variant die expliciet moest plannen scoorde er niet beter door. Wat wél
// vertrouwen bouwt is zichtbaar maken WAT de chat heeft nagekeken. Dat zijn
// vier dingen, en ze staan hier bij elkaar zodat ChatPopup zelf klein blijft:
//
//  1. TOOLSTAPPEN als levende statusregel tijdens het wachten. Verklaart
//     meteen waarom een antwoord even duurt.
//  2. GETELD-BADGES bij een getal dat écht in de database geteld is, met het
//     regels-verschil als tooltip. Dat maakt de twee telsemantieken zichtbaar
//     in plaats van verwarrend (de judge zelf trapte er ooit in).
//  3. DE AFSLAG naar het tabblad met het filter voorgezet: "bekijk alle 43 op
//     de Skills-tab". Dit is de gemeten oplossing voor faalgeval T2-CUR-1,
//     waar de chat 8 van de 43 skills als de lijst presenteerde. De chat hoeft
//     geen lijsten uit te schrijven waar het scherm beter in is.
//  4. VERVOLGVRAGEN als eigen sectie onderaan, één per regel, zodat je nooit
//     met een leeg invoerveld blijft zitten.
//
// EEN AFSLAG WORDT ALLEEN AANGEBODEN ALS DE TAB HETZELFDE GETAL KAN TONEN.
// Draagt een telling een as die het scherm niet kent (min_gezag op bronnen,
// zonder_trend op skills, de contextlaag op trends), dan blijft de badge
// staan en vervalt de doorklik. Een doorklik die een ánder aantal toont is
// erger dan geen doorklik: dan is het getal juist niet meer na te tellen.

import { type ReactNode, useEffect, useRef, useState } from "react";

import type { ChatResponse, ChatStap, KaartLanding } from "../contracts";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { chart, color, radius, size, weight } from "./chatTokens";
import { Tooltip } from "./ui";

export type Telling = NonNullable<ChatResponse["tellingen"]>[number];

/** Wat de gebruiker leest in plaats van een toolnaam. Ook de chatlogs van
 *  Beheer (W6 ronde 5) lezen deze lijst: zo heet een stap daar hetzelfde als
 *  op het bonnetje dat de gebruiker zag. */
export const TOOLTAAL: Record<string, string> = {
  search_trends: "zoekt in de trends",
  search_skills: "zoekt in de vaardigheden",
  search_sources: "zoekt in de brondocumenten",
  search_arbeidsmarkt: "zoekt in de arbeidsmarktwaarnemingen",
  get_trend_detail: "haalt de trend met zijn bewijs op",
  get_skill_detail: "haalt de vaardigheid met haar scores op",
  get_skills_for_trend: "volgt de brug van trend naar vaardigheden",
  get_trends_for_skill: "volgt de brug van vaardigheid naar trends",
  get_cijferreeks: "haalt de cijferreeks op",
  get_changes_since: "vergelijkt met de vorige meting",
  count_matching: "telt in de database",
  count_by_facet: "telt de hele as in één keer",
  search_funding: "zoekt in de subsidieregelingen",
  // Trendlaag ronde 3: bestaat alleen bij aan-stand van de schakelaar.
  count_trendlaag: "telt op de trendlaag",
};

/** Bij aan-stand van de trendlaag vinden de trendtools BEVINDINGEN (met hun
 *  trend en ontwikkeling als kader); het bonnetje zegt dat dan ook zo. */
const TOOLTAAL_TRENDLAAG: Record<string, string> = {
  search_trends: "zoekt in de bevindingen",
  get_trend_detail: "haalt de bevinding met haar kader op",
};

/** De stapnaam van een tool, in trendlaagwoorden als de schakelaar aan staat. */
export function toolTaal(tool: string): string {
  return (trendlaagAan() ? TOOLTAAL_TRENDLAAG[tool] : undefined) ?? TOOLTAAL[tool] ?? tool;
}

const SUBJECT = {
  trends: { tab: "trends", label: "Trends-tab", ding: "trends" },
  skills: { tab: "skills", label: "Skills-tab", ding: "vaardigheden" },
  bronnen: { tab: "sources", label: "Bronnen-tab", ding: "bronnen" },
  observaties: { tab: "arbeidsmarkt", label: "Arbeidsmarkt-tab", ding: "waarnemingen" },
  // Funding fase 5: search_funding telt wat de Funding-tab met hetzelfde filter toont.
  regelingen: { tab: "funding", label: "Funding-tab", ding: "regelingen" },
} as const;

function lijst(waarde: unknown): string[] | null {
  if (Array.isArray(waarde)) return waarde.map(String);
  if (typeof waarde === "string" && waarde) return [waarde];
  return null;
}

/**
 * Van een telling naar een doorklik — of naar `null` als de tab dit filter niet
 * kan reproduceren. Zie de kop van dit bestand: liever geen afslag dan een
 * afslag die een ander getal toont.
 */
export function naarLanding(t: Telling): KaartLanding | null {
  const f = (t.filter ?? {}) as Record<string, unknown>;
  const sleutels = Object.keys(f);
  const doel = SUBJECT[t.subject as keyof typeof SUBJECT];
  if (!doel) return null;

  if (t.subject === "skills") {
    const toegestaan = ["category", "status", "quick_win", "search"];
    if (sleutels.some((k) => !toegestaan.includes(k))) return null;
    return {
      soort: "tab",
      tab: "skills",
      filter: {
        category: lijst(f.category) ?? [],
        status: lijst(f.status) ?? [],
        quickWin: f.quick_win === true,
        zoek: typeof f.search === "string" ? f.search : "",
      },
    };
  }
  if (t.subject === "trends") {
    const toegestaan = ["category", "kind", "level", "search", "laag"];
    if (sleutels.some((k) => !toegestaan.includes(k))) return null;
    // De Trends-tab toont ALLEEN dragers. Telde de chat expliciet de
    // contextlaag, dan bestaat die weergave daar niet en vervalt de doorklik.
    if (f.laag !== undefined && f.laag !== "drager") return null;
    return {
      soort: "tab",
      tab: "trends",
      filter: {
        category: lijst(f.category) ?? [],
        level: lijst(f.level) ?? [],
        // Geen kind in de telling = de chat telde over BEIDE soorten. De tab
        // opent dan op "Alles", anders zou hij alleen de vaktrends tonen en
        // een kleiner getal geven dan de chat noemde.
        kind: typeof f.kind === "string" ? f.kind : "",
        zoek: typeof f.search === "string" ? f.search : "",
      },
    };
  }
  if (t.subject === "bronnen") {
    const toegestaan = ["uitgever", "search", "jaar_vanaf", "vanaf_dagen"];
    if (sleutels.some((k) => !toegestaan.includes(k))) return null;
    let van = "";
    if (typeof f.jaar_vanaf === "number") van = `${f.jaar_vanaf}-01-01`;
    if (typeof f.vanaf_dagen === "number") {
      const d = new Date();
      d.setDate(d.getDate() - f.vanaf_dagen);
      van = d.toISOString().slice(0, 10);
    }
    return {
      soort: "tab",
      tab: "sources",
      filter: {
        uitgever: lijst(f.uitgever) ?? [],
        zoek: typeof f.search === "string" ? f.search : "",
        van,
      },
    };
  }
  if (t.subject === "regelingen") {
    // Funding fase 5: de chat-tool levert het filter al in de namen van de
    // Funding-tab. `sluitBinnen` kan die tab niet voorzetten: dan alleen de badge.
    const toegestaan = ["sector", "status", "thema", "verstrekkerType", "venster", "zoek"];
    if (sleutels.some((k) => !toegestaan.includes(k))) return null;
    const filter: Record<string, unknown> = {};
    for (const k of ["sector", "status", "thema", "verstrekkerType", "venster"]) {
      const w = lijst(f[k]);
      if (w) filter[k] = w;
    }
    if (typeof f.zoek === "string" && f.zoek) filter.zoek = f.zoek;
    return { soort: "tab", tab: "funding", filter };
  }
  // Arbeidsmarkt: het scherm kent (nog) geen voorgezet filter, dus alleen de
  // badge. Eerlijk afwezig boven een doorklik die iets anders laat zien.
  return null;
}

/**
 * De tellingen die inline als badge mogen verschijnen. Alleen `count_matching`:
 * een facettelling levert tientallen waarden op en zou dan half het antwoord
 * onderstrepen. Facetten krijgen hun eigen chips onder het antwoord.
 */
export function inlineTellingen(tellingen: Telling[] | undefined): Telling[] {
  return (tellingen ?? []).filter((t) => t.tool === "count_matching" || t.tool === "search_funding");
}

/** Normaliseert "1.762" naar 1762 — punt is in het Nederlands de duizendtalscheiding. */
export function alsGetal(tekst: string): number | null {
  if (!/^\d[\d.]*$/.test(tekst)) return null;
  const n = Number(tekst.replace(/\./g, ""));
  return Number.isFinite(n) ? n : null;
}

function badgeUitleg(t: Telling): ReactNode {
  const doel = SUBJECT[t.subject as keyof typeof SUBJECT];
  const regels: string[] = [
    `${t.aantal} ${doel?.ding ?? t.subject} voldoen aan het filter. ` +
      "Dit getal komt uit een telling in de database, niet uit de zoekresultaten.",
  ];
  if (typeof t.regels === "number" && t.regels !== t.aantal) {
    regels.push(
      `De lijstweergave toont ${t.regels} regels: onderliggende rijen tellen ` +
        "daar mee. Allebei waar, ze meten iets anders.",
    );
  }
  if (typeof t.op_de_tab === "number" && t.op_de_tab !== t.aantal) {
    regels.push(
      `Op de Trends-tab zie je hiervan ${t.op_de_tab} regels: dat scherm opent ` +
        "op het kernbeeld, waar alleen dragende trends een eigen regel zijn en " +
        "contexttrends eronder hangen als onderbouwing.",
    );
  }
  if (t.tip) regels.push(t.tip);
  return (
    <>
      {regels.map((r, i) => (
        <div key={i} style={{ marginTop: i ? 6 : 0 }}>
          {r}
        </div>
      ))}
    </>
  );
}

/** Het badge-vinkje achter een getal dat écht geteld is. */
export function GeteldBadge({ telling }: { telling: Telling }) {
  return (
    <Tooltip text={badgeUitleg(telling)} maxWidth={280}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          marginLeft: 4,
          fontSize: 9.5,
          fontWeight: weight.medium,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          color: color.accentGreen,
          background: "#ecfdf5",
          border: "1px solid #a7f3d0",
          borderRadius: radius.pill,
          padding: "1px 6px",
          verticalAlign: "middle",
          cursor: "help",
          whiteSpace: "nowrap",
        }}
      >
        ✓ geteld
      </span>
    </Tooltip>
  );
}

function AfslagKnop({
  tekst,
  onClick,
}: {
  tekst: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.pill,
        padding: "5px 12px",
        fontSize: size.meta,
        fontFamily: "inherit",
        color: color.ink,
        cursor: "pointer",
        transition: "all 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color.ink;
        e.currentTarget.style.background = color.surfaceAlt;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = color.border;
        e.currentTarget.style.background = color.surface;
      }}
    >
      {tekst} →
    </button>
  );
}

/**
 * De strook onder het antwoord: per telling een afslag naar het tabblad.
 * Facettellingen worden één regel met een chip per waarde.
 */
export function Afslagen({
  tellingen,
  onOpen,
}: {
  tellingen: Telling[] | undefined;
  onOpen?: (landing: KaartLanding) => void;
}) {
  if (!onOpen || !tellingen?.length) return null;

  const enkel = tellingen.filter((t) => t.tool === "count_matching" || t.tool === "search_funding");
  const facetten = tellingen.filter((t) => t.tool === "count_by_facet");
  const knoppen: ReactNode[] = [];

  for (const [i, t] of enkel.entries()) {
    const landing = naarLanding(t);
    const doel = SUBJECT[t.subject as keyof typeof SUBJECT];
    if (!landing || !doel) continue;
    // DE KNOP NOEMT HET GETAL DAT DAAR ÓÓK ECHT STAAT. Bij trends is dat wat
    // het kernbeeld toont, niet het totaal: contexttrends hangen daar als
    // onderbouwing onder hun ouder en zijn geen eigen regel. Zonder dit
    // onderscheid belooft de knop 302 en laat de kop 205 zien — en dan is het
    // getal in de chat juist niet meer na te tellen (gemeten 2026-08-25).
    const opDeTab = typeof t.op_de_tab === "number" ? t.op_de_tab : t.aantal;
    const ding = doel.ding;
    knoppen.push(
      <AfslagKnop
        key={`t${i}`}
        tekst={`bekijk alle ${opDeTab} ${ding} op de ${doel.label}`}
        onClick={() => onOpen(landing)}
      />,
    );
  }

  // Facetten: hooguit acht waarden als chip, de rest eerlijk als aantal.
  const facetChips: ReactNode[] = [];
  const TOON = 8;
  for (const [i, t] of facetten.slice(0, TOON).entries()) {
    const landing = naarLanding(t);
    const waarde = Object.values(t.filter ?? {})[0];
    const naam = Array.isArray(waarde) ? String(waarde[0]) : String(waarde ?? "");
    if (!landing || !naam) continue;
    facetChips.push(
      <AfslagKnop
        key={`f${i}`}
        tekst={`${naam} (${t.aantal})`}
        onClick={() => onOpen(landing)}
      />,
    );
  }
  const rest = facetten.length - Math.min(facetten.length, TOON);

  if (!knoppen.length && !facetChips.length) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Bekijk op het scherm
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {knoppen}
        {facetChips}
        {rest > 0 && (
          <span style={{ fontSize: 11.5, color: color.faint }}>
            en {rest} andere {rest === 1 ? "waarde" : "waarden"}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * "Verder verkennen" — de vervolgvragen, opnieuw ontworpen (Chat 2.0, punt 8
 * van de owner-feedback 26-08: "vervolgvragen moet mooier en beter gedesigned
 * worden"). Ruimere rijen, een ↳-icoon in een cirkeltje per vraag, hover licht
 * de rij op en kleurt de pijl ink. Eén vraag per regel blijft; klik verstuurt
 * direct (dat was al zo en dat blijft).
 */
export function Vervolgvragen({
  vragen,
  onKies,
  /** 4B (feedbackronde 27-08): routes het platform in, naast de vragen —
   *  afgeleid van wat dit antwoord aanraakte. Max ~4, anders is het een
   *  tweede menu. */
  routes,
}: {
  vragen: readonly string[] | undefined;
  onKies: (vraag: string) => void;
  routes?: readonly { label: string; doe: () => void }[];
}) {
  if (!vragen?.length && !routes?.length) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 2,
        }}
      >
        Verder verkennen
      </div>
      {routes && routes.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "6px 0 8px",
          }}
        >
          {routes.slice(0, 4).map((r) => (
            <button
              key={r.label}
              onClick={r.doe}
              style={{
                border: `1px solid ${color.border}`,
                borderRadius: radius.pill,
                background: color.surface,
                padding: "4px 11px",
                fontSize: 12,
                color: color.body,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              {r.label} →
            </button>
          ))}
        </div>
      )}
      <div style={{ borderTop: `1px solid ${color.lineSoft}` }}>
        {(vragen ?? []).map((v) => (
          <button
            key={v}
            onClick={() => onKies(v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              background: "none",
              border: "none",
              borderBottom: `1px solid ${color.lineSoft}`,
              padding: "11px 4px",
              fontSize: 13.5,
              fontWeight: weight.regular,
              fontFamily: "inherit",
              color: color.ink,
              cursor: "pointer",
              textAlign: "left",
              lineHeight: 1.5,
              transition: "background 0.12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = color.surfaceAlt;
              const pijl = e.currentTarget.querySelector<HTMLElement>("[data-pijl]");
              if (pijl) pijl.style.color = color.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              const pijl = e.currentTarget.querySelector<HTMLElement>("[data-pijl]");
              if (pijl) pijl.style.color = color.disabled;
            }}
          >
            <span
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: radius.pill,
                border: `1px solid ${color.border}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: color.soft,
                flexShrink: 0,
              }}
            >
              ↳
            </span>
            <span style={{ flex: 1 }}>{v}</span>
            <span data-pijl style={{ color: color.disabled, flexShrink: 0 }}>
              →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}


/**
 * De gebeurtenissen tot leesbare regels, in de volgorde waarin ze gebeurden.
 *
 * Een `ronde`-gebeurtenis krijgt zijn eigen regel. Die bestaat omdat de eerste
 * modelronde lang duurt en tot nu níéts meldde: de owner zag tientallen
 * seconden alleen drie puntjes (opname 0:04, "it only gives one step and then
 * it takes really long"). Wat er staat is wat er gebeurt - een modelronde met
 * gereedschap beschikbaar - en niet wat het model denkt; de grens uit §8d
 * (het bonnetje, niet de gedachten) blijft staan.
 */
/**
 * Wat een getal in de uitkomst TELT, per tool.
 *
 * Owner-opname rcgukmx6hc8l 1:48: "8 resultaten, 6 resultaten — seems a bit
 * low". Het getal was niet laag, het was naamloos: acht van de beste passages
 * uit 70.000 fragmenten leest anders dan "8 resultaten". De backend levert een
 * kaal `N resultaten` (chat.py `_samenvatting`) omdat hij de eenheid niet kent;
 * het scherm weet welke tool hij aanriep en kan hem dus benoemen.
 */
const EENHEID: Record<string, string> = {
  search_sources: "beste passages",
  search_trends: "trends gevonden",
  search_skills: "vaardigheden gevonden",
  search_arbeidsmarkt: "waarnemingen gevonden",
  search_funding: "regelingen gevonden",
};

/** "8 resultaten" -> "8 beste passages", als we de eenheid kennen. */
function hertaal(uitkomst: string, tool: string): string {
  const eenheid = trendlaagAan() && tool === "search_trends" ? "bevindingen gevonden" : EENHEID[tool];
  if (!eenheid) return uitkomst;
  const m = /^(\d+) resultaten$/.exec(uitkomst.trim());
  return m ? `${m[1]} ${eenheid}` : uitkomst;
}

/**
 * HET ONDERZOEKSVERSLAG (feedbackronde 27-08, B2 — owner: "ik wil meer
 * stappen, maar duidelijker"). Tot deze ronde vouwden identieke aanroepen
 * samen ("4× zoekt in de brondocumenten") — dat verborg juist het werk. Nu
 * krijgt élke aanroep zijn eigen regel mét argumenten in mensentaal,
 * gegroepeerd onder drie fasekoppen die het verhaal dragen:
 *
 *   Zoeken     de zoekslagen per laag, dead-ends gedempt maar zichtbaar
 *   Verdiepen  detailopvragen, de brug, cijferreeksen
 *   Natellen   de tellingen en controles
 *
 * De reden per stap komt DETERMINISTISCH uit de argumenten — bewust geen
 * chain-of-thought: die is eerder gemeten en voegde niets toe.
 */
const FASE_VAN: Record<string, string> = {
  search_sources: "Zoeken",
  search_trends: "Zoeken",
  search_skills: "Zoeken",
  search_arbeidsmarkt: "Zoeken",
  search_funding: "Zoeken",
  get_trend_detail: "Verdiepen",
  get_skill_detail: "Verdiepen",
  get_skills_for_trend: "Verdiepen",
  get_cijferreeks: "Verdiepen",
  get_changes_since: "Verdiepen",
  count_matching: "Natellen",
  count_by_facet: "Natellen",
  count_trendlaag: "Natellen",
};
const FASE_VOLGORDE = ["Zoeken", "Verdiepen", "Natellen"];

/** De argumenten van een toolaanroep in mensentaal: de zoekterm en de
 *  filters die de stap concreet maken ("op 'cloud' · categorie Cloud
 *  Computing"). Onbekende sleutels blijven weg — liever te weinig dan een
 *  regel machinetaal. */
export function argTekst(args?: Record<string, unknown> | null): string | null {
  if (!args) return null;
  const delen: string[] = [];
  const q = args["query"] ?? args["zoekterm"];
  if (typeof q === "string" && q.trim()) delen.push(`op "${q.trim()}"`);
  const LABELS: [string, string][] = [
    ["category", "categorie"], ["uitgever", "uitgever"],
    ["status", "status"], ["sector", "sector"], ["thema", "thema"],
    ["gebied", "gebied"], ["geo", "gebied"], ["regio", "regio"],
    ["indicator", "reeks"], ["level", "niveau"], ["sorteer", "gesorteerd op"],
    // count_trendlaag (alleen bij aan-stand van de trendlaag)
    ["onderwerp", "onderwerp"], ["soort", "soort"],
  ];
  for (const [sleutel, label] of LABELS) {
    const v = args[sleutel];
    if (typeof v === "string" && v.trim()) delen.push(`${label} ${v.trim()}`);
    if (typeof v === "number") delen.push(`${label} ${v}`);
  }
  return delen.length ? delen.join(" · ") : null;
}

export type OnderzoekRegel = {
  tekst: string;
  arg: string | null;
  uitkomst: string | null;
  ronde: number;
  /** Een stap die niets opleverde: zichtbaar maar gedempt — dat het model
   *  ergens zocht en niets vond is informatie, geen ruis. */
  dood: boolean;
};

export function onderzoeksfasen(
  stappen: ChatStap[],
): { fase: string; regels: OnderzoekRegel[] }[] {
  let ronde = 1;
  const regels: (OnderzoekRegel & { fase: string; klaar: boolean })[] = [];
  for (const stap of stappen) {
    if (stap.soort === "ronde") {
      ronde = stap.n;
      continue;
    }
    if (stap.soort !== "tool_start" && stap.soort !== "tool_klaar") continue;
    const fase = FASE_VAN[stap.tool] ?? "Verdiepen";
    const taal = toolTaal(stap.tool);
    if (stap.soort === "tool_start") {
      regels.push({
        fase,
        tekst: taal,
        arg: argTekst("args" in stap ? (stap.args as Record<string, unknown>) : null),
        uitkomst: null,
        ronde,
        dood: false,
        klaar: false,
      });
    } else {
      const open = regels.find((r) => r.tekst === taal && !r.klaar);
      const uit = hertaal(stap.samenvatting ?? "klaar", stap.tool);
      const dood = /^0(\s|$)/.test(uit.trim());
      if (open) {
        open.uitkomst = uit;
        open.klaar = true;
        open.dood = dood;
      } else {
        regels.push({ fase, tekst: taal, arg: null, uitkomst: uit, ronde, dood, klaar: true });
      }
    }
  }
  return FASE_VOLGORDE.map((fase) => ({
    fase,
    regels: regels
      .filter((r) => r.fase === fase)
      .map(({ tekst, arg, uitkomst, ronde: r, dood }) => ({ tekst, arg, uitkomst, ronde: r, dood })),
  })).filter((f) => f.regels.length > 0);
}

/**
 * Het bonnetje bij aan-stand van de trendlaag (CH-A, editie 15): één regel
 * "7 stappen nagekeken: zoekt in de bevindingen · weegt 5 trends · kiest 3 ·
 * telt op de trendlaag". Alleen wat de stappen werkelijk waren: de zoekslagen
 * (hun doel, in trendlaagwoorden via toolTaal), dan wat het kader zegt (de
 * trends die de tools tegenkwamen en die het antwoord koos), dan de tellingen.
 * De verdiepstappen staan in het uitgeklapte verslag, niet in deze ene regel.
 */
export function bonnetjeDelen(
  stappen: ChatStap[],
  laag: { gewogen: number; gekozen: number },
): string[] {
  const zoek: string[] = [];
  const tel: string[] = [];
  for (const stap of stappen) {
    if (stap.soort !== "tool_klaar") continue;
    const taal = toolTaal(stap.tool);
    const fase = FASE_VAN[stap.tool];
    if (fase === "Zoeken") {
      const doel = taal.replace(/^zoekt in /, "");
      if (!zoek.includes(doel)) zoek.push(doel);
    } else if (fase === "Natellen" && !tel.includes(taal)) {
      tel.push(taal);
    }
  }
  const getal = (n: number) => n.toLocaleString("nl-NL");
  const delen: string[] = [];
  // "zoekt in de bevindingen, de brondocumenten en de vaardigheden"
  if (zoek.length) delen.push(`zoekt in ${zoek.length > 1 ? `${zoek.slice(0, -1).join(", ")} en ${zoek.at(-1)}` : zoek[0]}`);
  if (laag.gewogen > 0) {
    delen.push(`weegt ${getal(laag.gewogen)} ${laag.gewogen === 1 ? "trend" : "trends"}`);
    delen.push(laag.gekozen > 0 ? `kiest ${getal(laag.gekozen)}` : "kiest er geen");
  }
  delen.push(...tel);
  return delen;
}

/**
 * DE MOTIE VAN DE STAPPENREGEL — vastgesteld in het motielab
 * (web/demo/chat-motie.html, ronde 28-08). Eén blok, zodat de waarden na te
 * stellen zijn zonder door het component te spitten. De keyframes staan in
 * styles.css, want die kunnen niet inline.
 *
 * TWEE VELDEN DOEN HIER NIETS en staan er alleen voor de herkomst:
 * `diktePct` gold voor de ring-, boog-, segmenten- en rimpelloader, en
 * `vervaagPx` voor de "recente stappen"-lijst. Met loader `morph` en uitklap
 * `fase` raakt geen van beide iets aan.
 */
export const motie = {
  loader: { soort: "morph", kleur: chart.teal, tempoMs: 1100, groottePx: 19, diktePct: 14 },
  // rustKleur is sterktePct 62 uitgerekend: de menging tussen color.soft en
  // een lichte grijstint. Vast opgeschreven, want een mengsom bij elke render
  // is rekenwerk voor een waarde die nooit verandert.
  glans: { aan: true, tempoMs: 1400, bandbreedtePct: 8, sterktePct: 62, rustKleur: "#a8aeb8" },
  wissel: { soort: "omhoog", duurMs: 320, verloop: "cubic-bezier(0.16, 1, 0.3, 1)" },
  uitklap: { weergave: "fase", duurMs: 340, staggerMs: 40, vervaagPx: 34 },
  taal: "zin",
  // De afspeelwachtrij (poetsronde 28-08): hoe lang elk moment minstens in
  // beeld staat en het inhaaltempo als een burst zich opstapelt. Het lab
  // speelde een stap per ~1,5 s; de echte gebeurtenissen komen in bursts.
  // De hartslag bij stilte is weg sinds de testfeedback van 03-11.
  cadans: { minMs: 1100, inhaalMs: 550 },
} as const;

/** De loader: één vorm die tussen cirkel en vierkant blijft zoeken. */
export function ChatLoader() {
  return (
    <span
      role="status"
      aria-label="Bezig met zoeken"
      style={{
        position: "relative",
        display: "block",
        flex: `0 0 ${motie.loader.groottePx}px`,
        width: motie.loader.groottePx,
        height: motie.loader.groottePx,
        color: motie.loader.kleur,
      }}
    >
      <span
        className="chat-loader-morph"
        style={{
          position: "absolute",
          inset: "10%",
          borderRadius: "50%",
          background: "currentColor",
          animation: `chatMorph ${motie.loader.tempoMs}ms ease-in-out infinite`,
        }}
      />
    </span>
  );
}

/**
 * DE AFSPEELWACHTRIJ (poetsronde 28-08). Tot deze ronde toonde de regel "de
 * laatst gestarte aanroep" — maar de gebeurtenissen komen niet in het tempo
 * van het motielab binnen, ze komen in BURSTS: parallelle tools starten in
 * dezelfde tel, en tussen twee modelrondes is het tientallen seconden stil.
 * Gemeten (Playwright-tijdlijn): van de ~8 stappen waren er 2 ooit zichtbaar
 * en stond de regel 30 seconden bevroren. De regel speelt de gebeurtenissen
 * daarom ÁF in plaats van ze te tonen: elk moment minstens `cadans.minMs` in
 * beeld en inhalen als de rij zich opstapelt. Bij stilte blijft de regel
 * staan (testfeedback 03-11: rouleren las als "glitchy").
 */
export type StapMoment = { key: string; tekst: string };

/** Alle vertoonbare momenten, chronologisch: starts, uitkomsten en rondes. */
export function stapMomenten(stappen: ChatStap[]): StapMoment[] {
  const uit: StapMoment[] = [];
  const lopend: { tool: string; tekst: string }[] = [];
  let n = 0;
  for (const stap of stappen) {
    if (stap.soort === "ronde" && stap.n > 1) {
      uit.push({ key: `r${stap.n}`, tekst: `ronde ${stap.n} · bekijkt wat er nog nodig is` });
      continue;
    }
    if (stap.soort === "tool_start") {
      const taal = toolTaal(stap.tool);
      const arg = argTekst("args" in stap ? (stap.args as Record<string, unknown>) : null);
      const tekst = arg ? `${taal} ${arg}` : taal;
      lopend.push({ tool: stap.tool, tekst });
      uit.push({ key: `s${n++}`, tekst });
      continue;
    }
    if (stap.soort === "tool_klaar") {
      const i = lopend.findIndex((l) => l.tool === stap.tool);
      const basis =
        i >= 0 ? lopend.splice(i, 1)[0]!.tekst : toolTaal(stap.tool);
      const uitkomst = hertaal(stap.samenvatting ?? "", stap.tool);
      // Een uitkomst is een eigen moment: "→ 16 trends gevonden" laat live
      // zien dat het werk iets opleverde — dat werd tot nu nooit getoond.
      if (uitkomst) uit.push({ key: `k${n++}`, tekst: `${basis} → ${uitkomst}` });
    }
  }
  return uit;
}

/** De speler zelf. `aan` is de bezig-stand: in een afgerond verslag draait
 *  er geen enkele timer. */
export function useStapCadans(stappen: ChatStap[], aan: boolean): string | null {
  const momenten = stapMomenten(stappen);
  const [idx, setIdx] = useState(-1);

  useEffect(() => {
    // Een nieuw antwoord begint met een lege stappenlijst: speler terug naar
    // het begin, anders speelt hij het vorige gesprek door.
    if (momenten.length === 0 && idx !== -1) setIdx(-1);
  }, [momenten.length, idx]);

  useEffect(() => {
    if (!aan || idx >= momenten.length - 1) return;
    const achterstand = momenten.length - 1 - idx;
    const wacht = idx < 0 ? 120 : achterstand > 3 ? motie.cadans.inhaalMs : motie.cadans.minMs;
    const t = window.setTimeout(() => {
      setIdx((i) => Math.min(i + 1, momenten.length - 1));
    }, wacht);
    return () => window.clearTimeout(t);
  }, [aan, idx, momenten.length]);

  // Geen hartslag meer die bij stilte door andere zinnen rouleert
  // (testfeedback 03-11, rij 5: "glitchy"). De regel blijft staan op de stap
  // die echt loopt; de glans op de regel laat zien dat er nog gewerkt wordt.
  if (!aan || idx < 0 || momenten.length === 0) return null;
  return momenten[Math.min(idx, momenten.length - 1)]!.tekst;
}

/**
 * De regel die vertelt wat er NU gebeurt. Twee bewegingen over elkaar: een
 * glans die door de woorden trekt zolang de stap loopt, en een wissel omhoog
 * zodra er een andere stap begint.
 *
 * De glans zit op een BINNENSTE span en niet op de laag zelf — anders vechten
 * de glans- en de wisselanimatie om dezelfde `animation`-eigenschap en valt de
 * wissel stil zodra de glans aanstaat.
 */
function ActieveRegel({ tekst }: { tekst: string }) {
  const [staat, setStaat] = useState({ huidig: tekst, vorig: null as string | null });
  const anker = useRef(Date.now());

  // Afgeleide staat bij een gewijzigde prop: de oude tekst moet nog één
  // wissel lang blijven staan om weg te kúnnen bewegen.
  if (staat.huidig !== tekst) {
    setStaat({ huidig: tekst, vorig: staat.huidig });
  }

  useEffect(() => {
    if (staat.vorig === null) return;
    const tijd = window.setTimeout(
      () => setStaat((s) => ({ huidig: s.huidig, vorig: null })),
      motie.wissel.duurMs,
    );
    return () => window.clearTimeout(tijd);
  }, [staat.vorig]);

  // De regel wordt bij elke stap opnieuw opgebouwd, dus de glans zou telkens
  // op nul beginnen en haar doortocht (1,4 s) nooit afmaken binnen één stap.
  // Een negatieve vertraging ter grootte van de al verstreken tijd laat hem
  // doorlopen alsof hij nooit weg was.
  const vertraging = -((Date.now() - anker.current) % motie.glans.tempoMs);

  const beweging = {
    animationDuration: `${motie.wissel.duurMs}ms`,
    animationTimingFunction: motie.wissel.verloop,
    animationFillMode: "both" as const,
  };

  return (
    <span style={{ position: "relative", display: "block", flex: "1 1 auto", minWidth: 0 }}>
      {staat.vorig !== null ? (
        <span
          key={staat.vorig}
          className="chat-stap-uit"
          style={{
            ...beweging,
            animationName: "chatStapUit",
            position: "absolute",
            inset: 0,
            color: color.soft,
          }}
        >
          {staat.vorig}
        </span>
      ) : null}
      <span
        key={staat.huidig}
        className={staat.vorig !== null ? "chat-stap-in" : undefined}
        style={{
          display: "block",
          ...(staat.vorig !== null ? { ...beweging, animationName: "chatStapIn" } : {}),
        }}
      >
        <span
          className="chat-glans"
          style={{
            backgroundImage:
              `linear-gradient(90deg, ${motie.glans.rustKleur} calc(50% - ${motie.glans.bandbreedtePct}%), ` +
              `${color.ink} 50%, ${motie.glans.rustKleur} calc(50% + ${motie.glans.bandbreedtePct}%))`,
            backgroundSize: "200% 100%",
            backgroundRepeat: "no-repeat",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            animation: `chatGlans ${motie.glans.tempoMs}ms linear infinite`,
            animationDelay: `${vertraging}ms`,
          }}
        >
          {staat.huidig}
        </span>
      </span>
    </span>
  );
}

/** Het verslag zelf: drie fasekoppen met daaronder élke aanroep voluit. De
 *  regels komen met een kleine verspringing binnen, zodat het uitklappen als
 *  één beweging leest in plaats van als een blok dat verschijnt.
 *
 *  Het verslag wordt alleen gemonteerd als het OPEN staat. Ingeklapt laten
 *  staan zou de lopende stap twee keer in de DOM zetten — één keer in de
 *  kopregel, één keer hier — en dat is precies wat een schermlezer dubbel
 *  voorleest. De prijs is dat de hoogte niet meevloeit; de verspringing per
 *  regel draagt de beweging. */
function FaseVerslag({ fasen }: { fasen: ReturnType<typeof onderzoeksfasen> }) {
  let n = 0;
  const komtBinnen = (i: number) => ({
    animation:
      `chatStapIn ${motie.uitklap.duurMs}ms ${motie.wissel.verloop} ` +
      `${i * motie.uitklap.staggerMs}ms both`,
  });

  return (
    <>
      {fasen.map((f) => (
        <div key={f.fase} style={{ marginBottom: 4 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: weight.medium,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: color.disabled,
              padding: "5px 0 1px",
              ...komtBinnen(n++),
            }}
          >
            {f.fase} · {f.regels.length}
          </div>
          {f.regels.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 7,
                fontSize: 11.5,
                color: color.faint,
                lineHeight: 1.9,
                ...komtBinnen(n++),
                // Niet via opacity: de binnenkomst-animatie eindigt op
                // opacity 1 en zou de demping van een dode stap wissen.
                filter: r.dood ? "opacity(0.55)" : undefined,
              }}
            >
              <span
                style={{
                  color: r.uitkomst === null ? color.disabled : color.accentGreen,
                  width: 10,
                  flexShrink: 0,
                }}
              >
                {r.uitkomst === null ? "·" : "✓"}
              </span>
              <span style={{ minWidth: 0 }}>
                {r.tekst}
                {r.arg ? <span style={{ color: color.disabled }}> {r.arg}</span> : null}
              </span>
              {r.uitkomst ? (
                <span style={{ color: color.soft, fontWeight: weight.regular, flexShrink: 0 }}>
                  {r.uitkomst}
                </span>
              ) : null}
              {r.ronde > 1 ? (
                <span style={{ fontSize: 10, color: color.disabled, flexShrink: 0 }}>
                  r{r.ronde}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * HET ONDERZOEKSVERSLAG OP HET SCHERM.
 *
 * Tot 28-08 stond tijdens het wachten álles uitgeklapt. Dat is omgedraaid na
 * de owner-opnames l1t4lbvfu3uk en 3k4gmzo9j4up: tijdens het wachten staat er
 * nu ÉÉN rustige regel — loader, wat er op dit moment gebeurt, en een chevron.
 * Het volledige verslag zit één klik verderop en is niets kwijtgeraakt; dat is
 * precies de ruil die de owner koos (uitklap = "fase", niet de kortere
 * recente-stappenlijst). Verdwijnen mag het nooit: het is de verantwoording.
 */
export function Toolstappen({
  stappen,
  bezig,
  ingeklapt = false,
}: {
  stappen: ChatStap[];
  bezig: boolean;
  ingeklapt?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Onvoorwaardelijk (hook-regel); zonder `bezig` draait er geen timer.
  const cadansTekst = useStapCadans(stappen, bezig);
  const fasen = onderzoeksfasen(stappen);
  const alle = fasen.flatMap((f) => f.regels);

  if (!alle.length) {
    if (!bezig) return null;
    // De eerste modelronde duurt lang en meldde ooit niets (owner-opname
    // 0:04); een ronde-gebeurtenis zonder toolstappen verdient dus zijn
    // eigen melding in plaats van een kale loader. Mét de glans: dit is
    // dezelfde wachtstand als de stappenregel, alleen zonder stappen.
    const ronde = stappen.some((s) => s.soort === "ronde");
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", fontSize: 12 }}
      >
        <ChatLoader />
        <ActieveRegel
          tekst={ronde ? "leest je vraag en kiest gereedschap" : "Denkt na over je vraag…"}
        />
      </div>
    );
  }

  const gedaan = alle.filter((r) => r.uitkomst !== null).length;

  // "0 stappen nagekeken" verbergen (owner-opname 1:48): een bonnetje van
  // nul regels is geen bonnetje.
  if (ingeklapt && gedaan === 0) return null;

  // Als blok in een antwoord (ChatBlokken) staat het verslag altijd open —
  // daar is geen regel die het samenvat.
  const altijdOpen = !bezig && !ingeklapt;
  const uitgeklapt = altijdOpen || open;

  const kopknop = {
    display: "flex",
    alignItems: "center",
    gap: 9,
    width: "100%",
    background: "none",
    border: "none",
    padding: "6px 0",
    fontFamily: "inherit",
    textAlign: "left" as const,
    cursor: "pointer",
  };

  return (
    <div style={{ padding: "6px 0 8px" }}>
      {bezig ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{ ...kopknop, fontSize: 13 }}
        >
          <ChatLoader />
          <ActieveRegel tekst={cadansTekst ?? "onderzoekt je vraag"} />
          <span style={{ color: color.disabled, flexShrink: 0 }}>{open ? "▴" : "▾"}</span>
        </button>
      ) : null}

      {!bezig && ingeklapt ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{ ...kopknop, fontSize: 11.5, color: color.faint }}
        >
          <span style={{ color: color.accentGreen }}>✓</span>
          <span>
            {gedaan} {gedaan === 1 ? "stap" : "stappen"} nagekeken
          </span>
          <span style={{ color: color.disabled }}>{open ? "▴" : "▾"}</span>
        </button>
      ) : null}

      {uitgeklapt ? <FaseVerslag fasen={fasen} /> : null}
    </div>
  );
}

/**
 * Het laag-label op een bron-chip: staat dit document op de Bronnen-tab, of is
 * het achtergrond-/arbeidsmarktmateriaal? Dat onderscheid was er wel in de data
 * en niet op het scherm, terwijl het precies de vraag beantwoordt die faalgeval
 * T1-DIR-3 opriep: van wie komt dit, en hoe zwaar weegt het?
 */
export function LaagChip({ laag, sectie }: { laag?: string | null; sectie?: string | null }) {
  if (!laag && !sectie) return null;
  const context = laag === "context";
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {laag && (
        <Tooltip
          maxWidth={260}
          text={
            context
              ? "Contextlaag: dit document kwam door de bronnenrubriek maar draagt geen " +
                "gepubliceerde conclusie. Het staat niet op de Bronnen-tab; als achtergrond " +
                "telt het mee, als hoofdbewijs niet."
              : "Gepubliceerd: dit document staat op de Bronnen-tab en draagt bewijs onder " +
                "een trend of vaardigheid."
          }
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: weight.medium,
              color: context ? color.accentAmber : color.soft,
              background: context ? color.accentAmberBg : color.lineFaint,
              border: `1px solid ${context ? "#fde68a" : color.border}`,
              borderRadius: radius.pill,
              padding: "2px 7px",
              whiteSpace: "nowrap",
              cursor: "help",
            }}
          >
            {context ? "context" : "gepubliceerd"}
          </span>
        </Tooltip>
      )}
      {sectie && (
        <span
          title="Waar in het document dit fragment staat (structuurkaart)."
          style={{ fontSize: 10.5, color: color.faint, whiteSpace: "nowrap" }}
        >
          {sectie}
        </span>
      )}
    </span>
  );
}
